import { useState, useEffect } from 'react';
import { Dialog } from '@printtat/ui';
import { X, ShoppingCart } from 'lucide-react';
import { IconButton, Button, EmptyState, AlertDialog } from '@printtat/ui';
import { useCartStore } from '@/stores/cartStore';
import { cartApi } from '@/services/cartApi';
import { CartItemRow } from './CartItemRow';
import { CartSummary } from './CartSummary';

interface CartDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CartDrawer = ({ open, onOpenChange }: CartDrawerProps) => {
  const items = useCartStore((s) => s.items);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const clearCart = useCartStore((s) => s.clearCart);
  const subtotal = useCartStore((s) => s.subtotal());
  const itemCount = useCartStore((s) => s.itemCount());
  const [clearConfirm, setClearConfirm] = useState(false);
  const [hasBlockers, setHasBlockers] = useState(false);

  // Validate cart when drawer opens
  useEffect(() => {
    if (!open || items.length === 0) {
      setHasBlockers(false);
      return;
    }
    const validate = async () => {
      try {
        const result = await cartApi.validate();

        // Track blockers for disabling checkout
        setHasBlockers(result.changes.some((c) => c.type !== 'price_changed'));

        // Update prices locally so totals reflect current prices
        const priceChanges = result.changes.filter(
          (c) => c.type === 'price_changed',
        );
        if (priceChanges.length > 0) {
          useCartStore.setState((state) => ({
            items: state.items.map((item) => {
              const change = priceChanges.find((c) => c.itemId === item.id);
              return change?.newPrice != null
                ? { ...item, stalePriceFrom: item.unitPrice, unitPrice: change.newPrice, isStale: true }
                : item;
            }),
          }));
        }
      } catch {
        // Silently fail
      }
    };
    validate();
  }, [open, items.length]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-50" />
        <Dialog.Content className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right duration-300">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Dialog.Title className="text-base font-semibold text-gray-900">
                Cart ({itemCount})
              </Dialog.Title>
              {items.length > 0 && (
                <Button
                  variant="ghost"
                  color="gray"
                  size="sm"
                  onClick={() => setClearConfirm(true)}
                  className="text-[11px] text-gray-400 hover:text-red-500"
                >
                  Clear cart
                </Button>
              )}
            </div>
            <Dialog.Close asChild>
              <IconButton size="sm">
                <X size={18} />
              </IconButton>
            </Dialog.Close>
          </div>

          <Dialog.Description className="sr-only">
            Your shopping cart
          </Dialog.Description>

          {/* Items */}
          <div className="flex-1 overflow-y-auto px-5">
            {items.length === 0 ? (
              <EmptyState
                icon={ShoppingCart}
                title="Your cart is empty"
                description="Add designs to your cart from the builder or products page"
                className="py-12"
              />
            ) : (
              <div className="divide-y divide-gray-100">
                {items.map((item) => (
                  <CartItemRow
                    key={item.id}
                    item={item}
                    onUpdateQuantity={updateQuantity}
                    onRemove={removeItem}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          {items.length > 0 && (
            <div className="px-5 pb-5">
              <CartSummary
                subtotal={subtotal}
                itemCount={itemCount}
                onClose={() => onOpenChange(false)}
                disabled={hasBlockers}
              />
            </div>
          )}

          {/* Clear confirmation */}
          <AlertDialog
            open={clearConfirm}
            onOpenChange={setClearConfirm}
            title="Clear cart"
            description="Are you sure you want to remove all items from your cart?"
            confirmText="Clear"
            variant="destructive"
            onConfirm={() => { clearCart(); setClearConfirm(false); }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
